import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { WarehouseService } from '../../../services/warehouse.service';
import { AddressInput } from '../../../interfaces/card';
import { toAddressInput } from '../../board-v3/address.utils';
import { Warehouse, WarehousePayload } from '../warehouse.types';

/** Creates or edits a warehouse: name, description, one address and whether its stock may go negative */
@Component({
  selector: 'app-warehouse-form',
  templateUrl: './warehouse-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class WarehouseFormComponent implements OnInit {
  @Input() warehouse: Warehouse | null = null;
  /** The saved warehouse */
  @Output() close = new EventEmitter<Warehouse | void>();

  private readonly warehouseService = inject(WarehouseService);

  protected readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    description: new FormControl<string | null>('', [Validators.maxLength(10000)]),
    /** The address editor works with a list, a warehouse has one address at most */
    addresses: new FormControl<AddressInput[]>([], { nonNullable: true }),
    allow_negative: new FormControl(false, { nonNullable: true }),
  });

  protected saving = false;

  ngOnInit(): void {
    if (!this.warehouse) return;

    const { address, ...warehouse } = this.warehouse;
    this.form.reset({ ...warehouse, addresses: address ? [toAddressInput(address)] : [] });
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;

    const { addresses, ...raw } = this.form.getRawValue();
    const payload: WarehousePayload = { ...raw, name: raw.name.trim(), description: raw.description?.trim() || null };
    // An untouched address is not sent again, an emptied one is removed
    if (!this.warehouse || this.form.controls.addresses.dirty) payload.address = addresses[0] ?? null;

    this.saving = true;
    this.warehouseService
      .saveWarehouse(this.warehouse?.id ?? null, payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (warehouse) => {
          this.warehouseService.success('stock.warehouse_form.saved');
          this.close.emit(warehouse);
        },
        error: (error) => this.warehouseService.error(error),
      });
  }
}
